// =============================================================================
// MeuDriver — M3: Instrutor — Emissão de Certificados
// =============================================================================

import { useState, useEffect } from 'react'
import { Award, CheckCircle2, AlertCircle } from 'lucide-react'
import { useAuth } from '@/hooks/useAuth'
import { supabase } from '@/lib/supabase'
import {
  useEligibleStudents,
  useIssuedCertificates,
  useIssueCertificate,
} from '@/hooks/useCertificates'
import { CertificateCard } from '@/components/shared/CertificateCard'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog'
import { getInitials } from '@/lib/utils'
import type { CertificateRow } from '@/hooks/useCertificates'

// ---------------------------------------------------------------------------
// Página
// ---------------------------------------------------------------------------

export default function InstructorIssueCertificate() {
  const { user } = useAuth()
  const [instructorId, setInstructorId] = useState<string | null>(null)

  // Resolve instructor id
  useEffect(() => {
    if (!user?.id) return
    supabase
      .from('instructors')
      .select('id')
      .eq('profile_id', user.id)
      .maybeSingle()
      .then(({ data }) => setInstructorId(data?.id ?? null))
  }, [user?.id])

  const { data: eligible, isLoading: eligibleLoading } = useEligibleStudents(instructorId)
  const { data: issued, isLoading: issuedLoading } = useIssuedCertificates(instructorId)
  const { mutate: issueCert, isPending: issuing } = useIssueCertificate()

  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean
    studentId: string
    studentName: string | null
    totalHours: number
  }>({ open: false, studentId: '', studentName: null, totalHours: 0 })

  const [previewCert, setPreviewCert] = useState<CertificateRow | null>(null)

  function handleIssue() {
    if (!instructorId) return
    issueCert(
      {
        studentId: confirmDialog.studentId,
        instructorId,
        totalHours: confirmDialog.totalHours,
      },
      {
        onSuccess: () => {
          setConfirmDialog((s) => ({ ...s, open: false }))
        },
      },
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
          <Award className="h-6 w-6 text-teal-600" />
          Certificados
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          Emita certificados para alunos com 10 ou mais aulas concluídas.
        </p>
      </div>

      {/* Alunos elegíveis */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Alunos elegíveis</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {eligibleLoading ? (
            <div className="p-4 space-y-3">
              {[1, 2, 3].map((i) => <Skeleton key={i} className="h-16 w-full" />)}
            </div>
          ) : !eligible || eligible.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center px-4">
              <AlertCircle className="h-8 w-8 text-gray-300 mb-3" />
              <p className="font-medium text-gray-500">Nenhum aluno elegível</p>
              <p className="text-sm text-muted-foreground mt-1">
                Alunos com 10 ou mais aulas concluídas aparecem aqui.
              </p>
            </div>
          ) : (
            <div className="divide-y">
              {eligible.map((s) => (
                <div
                  key={s.student_id}
                  className="flex items-center gap-4 px-5 py-4"
                  data-testid="eligible-student-row"
                >
                  <Avatar className="h-10 w-10 shrink-0">
                    <AvatarFallback className="bg-teal-100 text-teal-700 font-semibold text-sm">
                      {getInitials(s.full_name ?? 'A')}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-gray-900 dark:text-white truncate">
                      {s.full_name ?? 'Aluno'}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {s.completed_lessons} aulas · {s.total_hours}h
                    </p>
                  </div>
                  {s.already_certified ? (
                    <Badge className="bg-green-100 text-green-700 border-green-200 shrink-0 gap-1">
                      <CheckCircle2 className="h-3 w-3" />
                      Certificado emitido
                    </Badge>
                  ) : (
                    <Button
                      size="sm"
                      data-testid="issue-cert-btn"
                      onClick={() =>
                        setConfirmDialog({
                          open: true,
                          studentId: s.student_id,
                          studentName: s.full_name,
                          totalHours: s.total_hours,
                        })
                      }
                    >
                      Emitir
                    </Button>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Certificados já emitidos */}
      {issued && issued.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Certificados emitidos</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {issuedLoading ? (
              <div className="p-4 space-y-3">
                {[1, 2].map((i) => <Skeleton key={i} className="h-14 w-full" />)}
              </div>
            ) : (
              <div className="divide-y">
                {issued.map((cert) => {
                  const student = cert.student as { full_name?: string | null } | null
                  return (
                    <div
                      key={cert.id}
                      className="flex items-center gap-4 px-5 py-4"
                      data-testid="issued-cert-row"
                    >
                      <Avatar className="h-9 w-9 shrink-0">
                        <AvatarFallback className="bg-gray-100 text-gray-600 font-semibold text-sm">
                          {getInitials(student?.full_name ?? 'A')}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                          {student?.full_name ?? 'Aluno'}
                        </p>
                        <p className="text-xs font-mono text-muted-foreground">
                          {cert.certificate_number}
                        </p>
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setPreviewCert(cert)}
                        data-testid="view-cert-btn"
                      >
                        Visualizar
                      </Button>
                    </div>
                  )
                })}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Dialog de confirmação de emissão */}
      <Dialog
        open={confirmDialog.open}
        onOpenChange={(o) => setConfirmDialog((s) => ({ ...s, open: o }))}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirmar emissão de certificado</DialogTitle>
            <DialogDescription>
              Você está prestes a emitir um certificado para{' '}
              <strong>{confirmDialog.studentName ?? 'este aluno'}</strong>.
              Esta ação não pode ser desfeita.
            </DialogDescription>
          </DialogHeader>
          <div className="rounded-lg bg-teal-50 border border-teal-100 p-4 text-sm space-y-1">
            <p>
              <span className="font-medium">Aluno:</span> {confirmDialog.studentName}
            </p>
            <p>
              <span className="font-medium">Total de horas:</span> {confirmDialog.totalHours}h
            </p>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setConfirmDialog((s) => ({ ...s, open: false }))}
            >
              Cancelar
            </Button>
            <Button
              onClick={handleIssue}
              disabled={issuing}
              data-testid="confirm-issue-btn"
              className="bg-teal-600 hover:bg-teal-700"
            >
              {issuing ? 'Emitindo...' : 'Emitir certificado'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog de visualização do certificado */}
      <Dialog
        open={!!previewCert}
        onOpenChange={(o) => { if (!o) setPreviewCert(null) }}
      >
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Certificado</DialogTitle>
          </DialogHeader>
          {previewCert && <CertificateCard certificate={previewCert} />}
        </DialogContent>
      </Dialog>
    </div>
  )
}
